import { InfoOutlineIcon, LockIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Text,
  Tooltip,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { KYDEventRealtimeMetrics } from "@common/interfaces/KYDEvent";
import { AdminTicketType, KYDTicketType } from "@common/interfaces/KYDTicket";
import {
  DragControls,
  Reorder,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import { useCallback } from "react";
import {
  FaClock,
  FaGripVertical,
  FaHourglass,
  FaHourglassHalf,
} from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { TieredTicketTypes } from "src/utils";

const Item = chakra(Reorder.Item);

const badgeForStatus = (ticketType: KYDTicketType) => {
  let bg = "gray";
  let name = "N/A";

  if (ticketType.sold_out) {
    bg = "orange";
    name = "Sold Out";
  } else {
    switch (ticketType.status) {
      case "DRAFT":
        bg = "blue";
        name = "Draft";

        break;
      case "PROCESSING":
        bg = "yellow";
        name = "Processing";

        break;
      case "OPEN":
        bg = "green";
        name = "On Sale";

        break;
      case "OFFSALE":
        bg = "red";
        name = "Off Sale";
        break;

      case "DISABLED":
        if (ticketType.tier && ticketType.tier > 0) {
          bg = "gray";
          name = "Not Active";
        }

        break;
      default: {
      }
    }
  }
  return (
    <Box key={ticketType.id}>
      <Badge
        justifyItems={"center"}
        maxW={"100px"}
        size={"xl"}
        colorScheme={bg}
      >
        {name}
      </Badge>
      {(ticketType.status === "OPEN" || ticketType.status === "DISABLED") &&
      ticketType.display_off_sale_at ? (
        <Text fontSize={"xs"} color={"gray.500"}>
          Off sale {ticketType.display_off_sale_at}
        </Text>
      ) : null}
      {(ticketType.status === "OFFSALE" || ticketType.status === "DISABLED") &&
      ticketType.display_on_sale_at ? (
        <Text fontSize={"xs"} color={"gray.500"}>
          On sale {ticketType.display_on_sale_at}
        </Text>
      ) : null}
    </Box>
  );
};

const TicketTypeDraggable = ({
  ticketType,
  tiers,
  onEdit,
  onUpdate,
  isLoading,
  readOnly = false,
  realtimeMetrics,
}: {
  onEdit: (tt: AdminTicketType) => void;
  onUpdate: () => void;
  ticketType: AdminTicketType;
  isLoading: boolean;
  readOnly: boolean;
  tiers: TieredTicketTypes;
  realtimeMetrics?: KYDEventRealtimeMetrics;
}) => {
  const dragControls = useDragControls();
  const y = useMotionValue(0);

  const handleMouseDownReorder = useCallback(
    (e: React.PointerEvent, item: any, dc: DragControls) => {
      dc.start(e);
      document.addEventListener(
        "mouseup",
        () => {
          onUpdate();
        },
        { once: true }
      );
    },
    [onUpdate]
  );

  const isWaitlistEnabled = (ticketType: AdminTicketType) => {
    return ticketType?.waitlist_status === "OPEN";
  };

  const isUpdatingQuantity = "quantity_update" in ticketType;

  return (
    <Item
      userSelect={"none"}
      display="table-row"
      key={ticketType.id}
      id={ticketType.id}
      value={ticketType.id}
      dragListener={false}
      style={{ y }}
      borderBottomWidth={1}
      borderBottomColor={"gray.100"}
      dragControls={dragControls}
    >
      <VStack
        p={4}
        display={{ base: "none", md: "table-cell" }}
        alignContent={"center"}
        justifyContent="center"
        height={"100%"}
        cursor={"grab"}
        onPointerDown={(e) => {
          handleMouseDownReorder(e, {}, dragControls);
        }}
        h="50px"
        w={["40px", null, "25px"]}
        justify={"center"}
      >
        {readOnly ? null : <Icon color="gray.400" as={FaGripVertical} />}
      </VStack>
      <Stack
        display="table-cell"
        alignContent={"center"}
        gap={2}
        pb={ticketType.code ? 2 : 0}
      >
        <VStack
          w="full"
          alignItems={"flex-start"}
          justifyContent="center"
          gap={2}
          minHeight="60px"
          py={2}
          paddingRight={2}
        >
          <Stack>
            <HStack alignItems="center" flexWrap="wrap">
              {ticketType.hidden ? (
                <LockIcon fontSize={"xs"} color={"gray.400"} />
              ) : null}
              <Text fontWeight="medium" wordBreak="break-word">
                {ticketType.name}
              </Text>
            </HStack>
            {isUpdatingQuantity ? (
              <HStack>
                <Spinner flexShrink={0} size="xs" color="gray.500" />
                <Text fontSize={["xs", null, "sm"]} color="gray.500">
                  Quantity updating
                </Text>
              </HStack>
            ) : null}
          </Stack>
          {ticketType.code ? (
            <Badge
              textAlign={"center"}
              maxW={"100px"}
              isTruncated
              colorScheme="purple"
            >
              {ticketType.code}
            </Badge>
          ) : null}
        </VStack>
        <VStack paddingRight={2} w="full" alignItems={"flex-start"}>
          {tiers[ticketType.id]
            ? tiers[ticketType.id]?.map((tier) => (
                <VStack
                  key={tier.id}
                  gap={2}
                  minHeight="60px"
                  py={2}
                  justifyContent={"center"}
                  w="full"
                  alignItems={"flex-start"}
                >
                  <HStack alignItems="center" flexWrap="wrap">
                    {tier.hidden ? (
                      <LockIcon fontSize={"xs"} color={"gray.400"} />
                    ) : null}
                    <Text fontWeight="medium" wordBreak="break-word">
                      {tier.name}
                    </Text>
                  </HStack>
                  {"quantity_update" in tier ? (
                    <HStack>
                      <Spinner flexShrink={0} size="xs" color="gray.500" />
                      <Text fontSize={["xs", null, "sm"]} color="gray.500">
                        Quantity updating
                      </Text>
                    </HStack>
                  ) : null}
                  {tier.code ? (
                    <Badge
                      textAlign={"center"}
                      maxW={"100px"}
                      isTruncated
                      colorScheme="purple"
                    >
                      {tier.code}
                    </Badge>
                  ) : null}
                </VStack>
              ))
            : null}
        </VStack>
      </Stack>
      <Stack display="table-cell" alignContent={"center"} gap={2} height="1px">
        <Box
          display="flex"
          justifyContent={"space-around"}
          flexDirection={"column"}
          height="100%"
        >
          <Text
            fontWeight="medium"
            minHeight="60px"
            display="flex"
            alignItems={"center"}
          >
            {ticketType.free ? "FREE" : ticketType.display_price}
          </Text>
          {tiers[ticketType.id]
            ? tiers[ticketType.id].map((tier) => (
                <Text
                  alignItems={"center"}
                  key={tier.id}
                  fontWeight="medium"
                  minHeight="60px"
                  display="flex"
                >
                  {tier.free ? "FREE" : tier.display_price}
                </Text>
              ))
            : null}
        </Box>
      </Stack>

      <Stack display="table-cell" alignContent={"center"} height="1px">
        <Box
          display="flex"
          justifyContent={"space-around"}
          flexDirection={"column"}
          height="100%"
        >
          {realtimeMetrics && realtimeMetrics.ticket_types[ticketType.id] ? (
            <>
              <VStack
                spacing={1}
                className="flex"
                w={"full"}
                alignItems={"flex-start"}
              >
                <HStack>
                  <Text color="muted">
                    {
                      realtimeMetrics.ticket_types[ticketType.id]
                        ?.tickets_display
                    }
                  </Text>
                  <Text>/</Text>
                  <Text color="muted">{ticketType.quantity}</Text>
                </HStack>
                {tiers[ticketType.id] ? null : realtimeMetrics.ticket_types[
                    ticketType.id
                  ]?.waitlist_buy_pool_tickets > 0 &&
                  isWaitlistEnabled(ticketType) ? (
                  <Tooltip
                    label={
                      "Number of tickets for this ticket type requested on the waitlist"
                    }
                  >
                    <Box className="flex">
                      <HStack
                        borderWidth={1}
                        borderColor="gray.200"
                        rounded={"sm"}
                        px={2}
                        flexShrink={0}
                        flexGrow={0}
                      >
                        <FaHourglassHalf fontSize="0.6rem" />
                        <Text fontSize={"x-small"}>
                          {
                            realtimeMetrics.ticket_types[ticketType.id]
                              ?.waitlist_buy_pool_tickets_display
                          }
                        </Text>
                      </HStack>
                    </Box>
                  </Tooltip>
                ) : null}
              </VStack>

              {tiers[ticketType.id]
                ? tiers[ticketType.id].map((tier, idx) => (
                    <VStack
                      key={tier.id}
                      spacing={1}
                      className="flex"
                      w={"full"}
                      alignItems={"flex-start"}
                    >
                      <HStack key={tier.id} flexGrow={0} flexShrink={0}>
                        <Text color="muted">
                          {realtimeMetrics &&
                          realtimeMetrics.ticket_types[tier.id]
                            ? realtimeMetrics.ticket_types[tier.id]
                                .tickets_display
                            : tier.total_tickets_purchased_display}
                        </Text>
                        <Text>/</Text>
                        <Text color="muted">{tier.quantity}</Text>
                      </HStack>
                      {idx + 1 === tiers[ticketType.id].length &&
                      isWaitlistEnabled(tier) ? (
                        <Tooltip
                          label={
                            "Number of tickets for this ticket type requested on the waitlist"
                          }
                        >
                          <Box className="flex">
                            <HStack
                              borderWidth={1}
                              borderColor="gray.200"
                              rounded={"sm"}
                              px={2}
                            >
                              <FaHourglassHalf fontSize="0.6rem" />
                              <Text fontSize={"x-small"}>
                                {
                                  realtimeMetrics.ticket_types[tier.id]
                                    ?.waitlist_buy_pool_tickets_display
                                }
                              </Text>
                            </HStack>
                          </Box>
                        </Tooltip>
                      ) : null}
                    </VStack>
                  ))
                : null}
            </>
          ) : (
            <>
              <HStack>
                <Text color="muted">
                  {ticketType.total_tickets_purchased_display}
                </Text>
                {ticketType.sold_out &&
                ticketType.total_tickets_purchased !== ticketType.quantity ? (
                  <Tooltip
                    hasArrow
                    label="This ticket type is currently sold out, but it can take up to 30 minutes for this metric to update."
                    fontSize="md"
                  >
                    <InfoOutlineIcon />
                  </Tooltip>
                ) : null}
              </HStack>
              {tiers[ticketType.id]
                ? tiers[ticketType.id].map((tier) => (
                    <HStack key={tier.id}>
                      <Text color="muted">
                        {realtimeMetrics &&
                        realtimeMetrics.ticket_types[tier.id]
                          ? realtimeMetrics.ticket_types[tier.id]
                              .tickets_display
                          : tier.total_tickets_purchased_display}
                      </Text>
                      {tier.sold_out &&
                      tier.total_tickets_purchased !== tier.quantity ? (
                        <Tooltip
                          hasArrow
                          label="This ticket type is currently sold out, but it can take up to 30 minutes for this metric to update."
                          fontSize="md"
                        >
                          <InfoOutlineIcon />
                        </Tooltip>
                      ) : null}
                    </HStack>
                  ))
                : null}
            </>
          )}
        </Box>
      </Stack>
      <Stack display="table-cell" alignContent={"center"} height="1px">
        <Box
          display="flex"
          justifyContent={"space-around"}
          flexDirection={"column"}
          height="100%"
        >
          {realtimeMetrics && realtimeMetrics.ticket_types[ticketType.id] ? (
            <>
              <Text>
                {realtimeMetrics.ticket_types[ticketType.id]?.sales_display}
              </Text>
              {tiers[ticketType.id]
                ? tiers[ticketType.id].map((tier) => (
                    <Text key={tier.id} color="muted">
                      {realtimeMetrics.ticket_types[tier.id]?.sales_display}
                    </Text>
                  ))
                : null}
            </>
          ) : (
            <>
              <Text color="muted">{ticketType.total_gmv_display}</Text>
              {tiers[ticketType.id]
                ? tiers[ticketType.id].map((tier) => (
                    <Text key={tier.id} color="muted">
                      {tier.total_gmv_display}
                    </Text>
                  ))
                : null}
            </>
          )}
        </Box>
      </Stack>
      <Stack display="table-cell" alignContent={"center"} height="1px">
        <Box
          display="flex"
          justifyContent={"space-around"}
          flexDirection={"column"}
          height="100%"
        >
          {badgeForStatus(ticketType)}
          {tiers[ticketType.id]
            ? tiers[ticketType.id].map((tier) => badgeForStatus(tier))
            : null}
        </Box>
      </Stack>
      <HStack
        p={4}
        display="table-cell"
        spacing={2}
        alignContent={"center"}
        textAlign={"right"}
      >
        {readOnly ? null : (
          <IconButton
            isDisabled={isLoading}
            onClick={() => onEdit(ticketType)}
            icon={<FiEdit2 fontSize="1.25rem" />}
            variant="ghost"
            aria-label="Edit member"
          />
        )}
      </HStack>
    </Item>
  );
};

export default TicketTypeDraggable;
